/**
 * Smooth moves for lists that place their rows by position (react-virtualized): when a row leaves or joins, the list
 * puts every other row in its new place at once. Read where the rows stood before (`rowTops`), let the list update,
 * then `slideRowsFrom` starts each row from where it stood and eases it into its new place, so a gap closes or opens
 * instead of jumping (Petr).
 *
 * Rows carry `data-slide-row` with something that names what they show (a task, a section), not their position.
 */
export type RowTops = Map<string, number>;

export const SLIDE_ROWS_MS = 320;

const rowsIn = (container: Element | null | undefined) =>
  Array.from(container?.querySelectorAll<HTMLElement>('[data-slide-row]') || []);

/** Where each row on screen stands, by what it shows. */
export const rowTops = (container: Element | null | undefined): RowTops => {
  const tops: RowTops = new Map();
  rowsIn(container).forEach(row => tops.set(row.dataset.slideRow!, row.getBoundingClientRect().top));
  return tops;
};

/**
 * Call once the list has put its rows in their new places, before they are painted there - in the same update, not on
 * the next frame, or a frame shows them already moved (Petr: "flashes once"). A row that wasn't on screen before moves
 * with the row above it, as it came into view with the move; one with nothing above it that moved is new, and fades in.
 */
export const slideRowsFrom = (container: Element | null | undefined, tops: RowTops) => {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const rows = rowsIn(container)
    .map(row => ({ row, top: row.getBoundingClientRect().top }))
    .sort((a, b) => a.top - b.top);
  let shift = 0;
  rows.forEach(({ row, top }) => {
    if (typeof row.animate !== 'function') return;
    const before = tops.get(row.dataset.slideRow!);
    shift = before !== undefined ? before - top : shift;
    const timing = { duration: SLIDE_ROWS_MS, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' };
    if (shift !== 0) {
      row.animate([{ transform: `translateY(${shift}px)` }, { transform: 'translateY(0)' }], timing);
    } else if (before === undefined) {
      row.animate([{ opacity: 0 }, { opacity: 1 }], timing);
    }
  });
};

/** Rows already sent off by `fadeRowsOut`, by key, with the copy left in their place until the gap closes. */
export const fadedOutRows = new Map<string, HTMLElement>();

/**
 * Rows leaving on a click (moved to the trash) start going at once: a copy of each, under the other rows, dims where
 * it stood while the row itself is hidden. The dimming is the compositor's, so it plays even while the list redraws
 * without them (about 0.1 s in a production build, 0.3 s in development - Petr: "a big lag"). The copy stays, dimmed,
 * until the list closes the gap (`finishFadedRows`), so no empty gap waits for the redraw (Petr: "a blank pause").
 * `then` (the move itself) runs once the first frame is on screen. The list clears the hidden rows when it redraws.
 */
export const fadeRowsOut = (keys: string[], then: () => void) => {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    then();
    return;
  }
  keys.forEach(key => {
    const row = document.querySelector<HTMLElement>(`[data-slide-row="${key}"]`);
    if (!row?.parentElement || typeof row.animate !== 'function') return;
    const copy = row.cloneNode(true) as HTMLElement;
    copy.removeAttribute('data-slide-row');
    copy.setAttribute('aria-hidden', 'true');
    copy.style.pointerEvents = 'none';
    row.parentElement.insertBefore(copy, row.parentElement.firstChild);
    row.style.visibility = 'hidden';
    fadedOutRows.set(key, copy);
    copy.animate([{ opacity: 1 }, { opacity: 0.35 }], { duration: 160, easing: 'ease-out', fill: 'forwards' });
  });
  if (typeof window.requestAnimationFrame !== 'function') then();
  else window.requestAnimationFrame(() => window.setTimeout(then, 0));
  // Should the list not redraw (nothing left it after all), nothing stays behind.
  window.setTimeout(() => keys.forEach(key => finishFadedRow(key)), 2000);
};

/** The copy left by `fadeRowsOut` fades the rest of the way as the rows below slide up over it, and goes. */
export const finishFadedRow = (key: string) => {
  const copy = fadedOutRows.get(key);
  fadedOutRows.delete(key);
  const row = document.querySelector<HTMLElement>(`[data-slide-row="${key}"]`);
  if (row?.style.visibility === 'hidden') row.style.visibility = '';
  if (!copy) return;
  copy
    .animate([{ opacity: 0.35 }, { opacity: 0 }], { duration: SLIDE_ROWS_MS, easing: 'ease-out', fill: 'forwards' })
    .finished.catch(() => undefined)
    .then(() => copy.remove());
};

// Tasks about to be made by Duplicate or a repeat, by name: the Task List unfolds them into place, as it does a task Undo
// brings back, instead of the rows jumping (Petr). Other new tasks (a reload, another view, the add row) come in as before.
let expectedNewTasks: { titles: string[]; until: number } | null = null;
export const expectNewTasks = (titles: string[]) => {
  expectedNewTasks = { titles: titles.map(title => title.trim()), until: Date.now() + 10000 };
};
/** Which of these just-arrived tasks are the expected ones; once found, nothing more is expected. */
export const takeExpectedNewTasks = <T extends { title: string }>(arrived: T[]): T[] => {
  if (!expectedNewTasks || Date.now() > expectedNewTasks.until) return [];
  const titles = expectedNewTasks.titles;
  const made = arrived.filter(task => titles.includes((task.title || '').trim()));
  if (made.length) expectedNewTasks = null;
  return made;
};

/** Rows the list drew again after `fadeRowsOut` hid them (its row elements are reused for the rows below). */
export const unhideRows = (container: Element | null | undefined) =>
  container?.querySelectorAll<HTMLElement>('[data-slide-row]').forEach(row => {
    if (row.style.visibility === 'hidden') row.style.visibility = '';
  });

/**
 * The sticky layer's sections (`data-slide-block`: a course, a group) are placed by the rows' heights, so when a row
 * leaves or joins they move at once, while the rows ease into place: a group's pill jumped onto the rows above it and
 * showed twice, over its own copy in the list (Petr). These two slide them with the rows. Each section's top is read
 * within its parent section, so a group in a course that moves too isn't moved twice.
 */
export type BlockTops = Map<string, number>;

const blocksIn = (container: Element | null | undefined) =>
  Array.from(container?.querySelectorAll<HTMLElement>('[data-slide-block]') || []);

const offsetIn = (block: HTMLElement) =>
  block.getBoundingClientRect().top - (block.parentElement?.getBoundingClientRect().top || 0);

export const blockTops = (container: Element | null | undefined): BlockTops => {
  const tops: BlockTops = new Map();
  blocksIn(container).forEach(block => tops.set(block.dataset.slideBlock!, offsetIn(block)));
  return tops;
};

export const slideBlocksFrom = (container: Element | null | undefined, tops: BlockTops) => {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  blocksIn(container).forEach(block => {
    const before = tops.get(block.dataset.slideBlock!);
    if (before === undefined || typeof block.animate !== 'function') return;
    const shift = before - offsetIn(block);
    if (Math.abs(shift) < 0.5) return;
    block.animate([{ transform: `translateY(${shift}px)` }, { transform: 'translateY(0)' }], {
      duration: SLIDE_ROWS_MS,
      easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)'
    });
  });
};

/**
 * A group whose last task leaves goes with it (by week), or keeps only its pill (by category: an empty group has no
 * column labels) - either way its header vanished at once while everything else slid (Petr). Copies of the group rows
 * on screen are taken before the list redraws (`groupCopies`); those whose group is gone after it, or lost its labels,
 * fade out where they stood (`fadeGoneGroups`), under the rows sliding up over them.
 */
export type GroupCopies = { key: string; copy: HTMLElement }[];

export const groupCopies = (container: Element | null | undefined): GroupCopies => {
  const shown = container?.getBoundingClientRect();
  if (!shown) return [];
  return rowsIn(container)
    .filter(row => row.dataset.slideRow!.startsWith('group-'))
    .filter(row => {
      const { top, bottom } = row.getBoundingClientRect();
      return bottom > shown.top && top < shown.bottom;
    })
    .map(row => ({ key: row.dataset.slideRow!, copy: row.cloneNode(true) as HTMLElement }));
};

export const fadeGoneGroups = (container: Element | null | undefined, copies: GroupCopies) => {
  if (!container || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const rows = container.querySelector('.ReactVirtualized__Grid__innerScrollContainer');
  copies.forEach(({ key, copy }) => {
    const now = container.querySelector(`[data-slide-row="${key}"]`);
    const lostLabels = !!now && !!copy.querySelector('.thead') && !now.querySelector('.thead');
    if (!rows || (now && !lostLabels) || typeof copy.animate !== 'function') return;
    copy.removeAttribute('data-slide-row');
    copy.setAttribute('aria-hidden', 'true');
    copy.style.pointerEvents = 'none';
    // The list keeps its own header copies hidden under the pinned layer; this one is all that's left of it, so shown.
    copy.querySelectorAll<HTMLElement>('.group-pill-row, .thead, .list-course-title').forEach(part => {
      part.style.visibility = 'visible';
    });
    rows.insertBefore(copy, rows.firstChild);
    copy
      // Labels give way to the empty group's "+ Add new task" in their place: quickly, so the two don't read as one.
      .animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: SLIDE_ROWS_MS * (lostLabels ? 0.45 : 0.8),
        easing: 'ease-out',
        fill: 'forwards'
      })
      .finished.catch(() => undefined)
      .then(() => copy.remove());
  });
};
